import { useState } from 'react';
import type { ReactNode } from 'react';
import type { TenantStatus } from '@yourstore/domain';
import { useAction, useApi, useRead } from '../../context';
import { Confirm, Empty, Failure, Loading } from '../../bits';
import { Chip, Panel, useToast } from '../../kit';
import { useLocale, useT } from '../../../messages';
import type { T } from '../../../messages';
import type {
  AppliedClosure,
  IntakeClosure,
  IntakeDayHours,
  IntakeDraft,
  IntakeRead,
  PageTemplate,
  ReviewQuestion,
  ReviewSection,
} from '../../../wire';
import { AnswerView } from './AnswerView';
import { StartFromAnswers } from './StartFromAnswers';
import { humanize, reasonWords, shortDate, weekdayName } from './words';

/**
 * **Their answers, as one guided screen** (`#/b/<id>/site/answers`).
 *
 * The old card showed the answers as a status pill, a JSON dump and five
 * collapsed drawers. This is the same material read in the order an operator
 * works through it: **their words → where each lands on the page → what needs a
 * hand**. The top of the screen says how many of each; the answers that could
 * not be placed are collected there first, with the reason in words, and are
 * flagged again where they sit in the form's own order.
 *
 * Every destination is still **the server's word** (`@yourstore/intake`'s
 * projection, computed by `admin-api`): this file contains no mapping from a
 * question to a place on the page and must not grow one. What it adds is the
 * reading order and the words for the reason codes.
 *
 * The two answers that are the **book** rather than the page — their week and
 * the days they are shut — and their menu, which becomes a catalog, live here
 * as the last sections, because they are answers too and this is where the
 * operator is reading them. Writing hours or closures changes what a guest can
 * book, so each is confirmed in words that say so, and each is fenced to the
 * setting-up stage (the database refuses them afterwards; the screen says so
 * before the click rather than after it).
 */
export function AnswersReview({
  tenantId,
  name,
  tenantStatus,
  templates,
  onBack,
  onStartPage,
  onSeedCatalog,
}: {
  tenantId: string;
  name: string;
  tenantStatus: TenantStatus;
  templates: readonly PageTemplate[];
  onBack: () => void;
  onStartPage: (draft: IntakeDraft) => void;
  onSeedCatalog: (fileText: string) => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const page = useRead(() => api.intake(tenantId), [tenantId]);
  const absent = page.failure !== null && page.failure.code === 'not_found';
  const intake = absent ? null : page.value;

  return (
    <div className="site-review">
      <div>
        <button type="button" className="linkish" onClick={onBack}>
          {t('site.back')}
        </button>
      </div>

      {page.failure !== null && !absent ? <Failure failure={page.failure} /> : null}

      {intake === null ? (
        page.loading ? (
          <Loading what={t('loading.intake')} />
        ) : absent ? (
          <Empty>{t('site.review.none')}</Empty>
        ) : null
      ) : (
        <Review
          tenantId={tenantId}
          name={name}
          tenantStatus={tenantStatus}
          intake={intake}
          templates={templates}
          onChanged={page.reload}
          onStartPage={onStartPage}
          onSeedCatalog={onSeedCatalog}
        />
      )}
    </div>
  );
}

function Review({
  tenantId,
  name,
  tenantStatus,
  intake,
  templates,
  onChanged,
  onStartPage,
  onSeedCatalog,
}: {
  tenantId: string;
  name: string;
  tenantStatus: TenantStatus;
  intake: IntakeRead;
  templates: readonly PageTemplate[];
  onChanged: () => void;
  onStartPage: (draft: IntakeDraft) => void;
  onSeedCatalog: (fileText: string) => void;
}): ReactNode {
  const t = useT();
  const { counts } = intake.review;
  const unplaced = intake.review.sections.flatMap((s) =>
    s.questions.filter((q) => q.placement === 'unplaced'),
  );

  return (
    <>
      <header className="site-review__head">
        <div>
          <h2>{t('site.review.title')}</h2>
          <p className="muted">{t('site.review.lead')}</p>
        </div>
        {intake.review.answered === 0 ? null : (
          <StartFromAnswers
            tenantId={tenantId}
            template={intake.template}
            templates={templates}
            primary
            onStartPage={onStartPage}
          />
        )}
      </header>

      <ul className="site-review__counts" aria-label={t('site.review.countsLabel')}>
        <li>
          <Chip tone={counts.applied === 0 ? 'plain' : 'ok'}>
            {t('site.review.onPage', { count: counts.applied })}
          </Chip>
        </li>
        <li>
          <Chip tone={counts.unplaced === 0 ? 'plain' : 'warn'}>
            {t('site.review.needHand', { count: counts.unplaced })}
          </Chip>
        </li>
        <li>
          <Chip tone="plain">{t('site.review.elsewhere', { count: counts.elsewhere })}</Chip>
        </li>
        <li>
          <Chip tone="plain">{t('site.review.unanswered', { count: counts.unanswered })}</Chip>
        </li>
      </ul>

      {unplaced.length === 0 ? null : (
        <Panel title={t('site.review.handTitle')} className="site-card site-card--warn">
          <p className="muted">{t('site.review.handLead')}</p>
          <ul className="site-hand">
            {unplaced.map((question) => (
              <li key={question.question_id}>
                <strong>{question.ask}</strong>
                <span className="site-hand__why">{reasonWords(t, question.reason)}</span>
                {question.detail === null ? null : <p className="muted">{question.detail}</p>}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {intake.review.sections.map((section) => (
        <SectionPanel key={section.section_id} section={section} />
      ))}

      {intake.operator_notes.length === 0 ? null : (
        <Panel title={t('site.review.notesTitle')} className="site-card">
          <p className="muted">{t('site.review.notesLead')}</p>
          <dl className="site-notes">
            {intake.operator_notes.map((note) => (
              <div key={note.question_id}>
                <dt>{note.ask}</dt>
                <dd>{note.answer}</dd>
              </div>
            ))}
          </dl>
        </Panel>
      )}

      <TheirWeek
        tenantId={tenantId}
        name={name}
        tenantStatus={tenantStatus}
        intake={intake}
        onChanged={onChanged}
      />

      <HerMenu tenantId={tenantId} onSeed={onSeedCatalog} />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* The form, section by section                                        */
/* ------------------------------------------------------------------ */

function SectionPanel({ section }: { section: ReviewSection }): ReactNode {
  const t = useT();
  return (
    <Panel
      title={section.title}
      className="site-card"
      actions={
        <span className="muted">
          {t('site.review.sectionCount', { answered: section.answered, total: section.total })}
        </span>
      }
    >
      <ul className="site-qa">
        {section.questions.map((question) => (
          <QuestionRow key={question.question_id} question={question} />
        ))}
      </ul>
    </Panel>
  );
}

function QuestionRow({ question }: { question: ReviewQuestion }): ReactNode {
  const t = useT();
  const unplaced = question.placement === 'unplaced';
  return (
    <li className={`site-qa__row${unplaced ? ' is-unplaced' : ''}`}>
      <div className="site-qa__words">
        <div className="site-qa__ask">{question.ask}</div>
        {question.answered ? (
          <div className="site-qa__answer">
            <AnswerView value={question.answer} />
          </div>
        ) : (
          <div className="muted">{t('site.review.notAnswered')}</div>
        )}
        {/*
          The addresses inside the answer, lifted out by the server: a brand's
          shop link is a button the page can have, and one buried in a list is
          one nobody opens. Each was validated `https:` before it was stored.
        */}
        {question.links.length === 0 ? null : (
          <ul className="site-qa__links">
            {question.links.map((link) => (
              <li key={link}>
                <a href={link} target="_blank" rel="noreferrer">
                  {link}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="site-qa__where">
        {unplaced ? (
          <>
            <Chip tone="warn">{reasonWords(t, question.reason)}</Chip>
            <span className="muted">→ {question.destination}</span>
            {question.detail === null ? null : <p className="muted">{question.detail}</p>}
          </>
        ) : !question.answered ? (
          <span className="muted">→ {question.destination}</span>
        ) : question.placement === 'elsewhere' ? (
          <>
            <Chip tone="plain">→ {question.destination}</Chip>
            <span className="muted">{t('site.review.notOnPage')}</span>
          </>
        ) : (
          <Chip tone="accent">→ {question.destination}</Chip>
        )}
      </div>
    </li>
  );
}

/* ------------------------------------------------------------------ */
/* Their week and the days they are shut                               */
/* ------------------------------------------------------------------ */

function weekSentence(hours: readonly IntakeDayHours[], locale: string, t: T): string {
  return hours
    .map(
      (day) =>
        `${weekdayName(day.weekday, locale)} ${
          day.closed || day.windows.length === 0
            ? t('site.hours.closed')
            : day.windows.map((w) => `${w.start}–${w.end}`).join(', ')
        }`,
    )
    .join(' · ');
}

function span(closure: IntakeClosure, locale: string): string {
  const from = shortDate(`${closure.from}T12:00:00Z`, locale);
  const to = shortDate(`${closure.to}T12:00:00Z`, locale);
  const days = from === to ? from : `${from} – ${to}`;
  return closure.reason === '' ? days : `${days} (${closure.reason})`;
}

function TheirWeek({
  tenantId,
  name,
  tenantStatus,
  intake,
  onChanged,
}: {
  tenantId: string;
  name: string;
  tenantStatus: TenantStatus;
  intake: IntakeRead;
  onChanged: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const locale = useLocale();
  const toast = useToast();
  const hoursAction = useAction();
  const closuresAction = useAction();
  const [asking, setAsking] = useState<'hours' | 'closures' | AppliedClosure | null>(null);

  const onboarding = tenantStatus === 'onboarding';
  const fence = onboarding ? undefined : t('site.week.fence', { name });

  const finish = (ok: boolean, done: string): void => {
    if (!ok) return;
    setAsking(null);
    toast(done);
    onChanged();
  };

  const applyHours = async (): Promise<void> => {
    const ok = await hoursAction.run(async () => {
      await api.applyIntakeHours(tenantId);
    });
    finish(ok, t('site.week.hoursApplied'));
  };

  const applyClosures = async (): Promise<void> => {
    let created = 0;
    const ok = await closuresAction.run(async () => {
      created = (await api.applyIntakeClosures(tenantId)).created;
    });
    finish(ok, t('site.week.closuresApplied', { count: created }));
  };

  const removeClosure = async (closure: AppliedClosure): Promise<void> => {
    const ok = await closuresAction.run(async () => {
      await api.removeIntakeClosure(tenantId, closure.blackout_id);
    });
    finish(ok, t('site.week.closureRemoved'));
  };

  return (
    <Panel title={t('site.week.title')} className="site-card">
      <p className="muted">{t('site.week.lead')}</p>
      {fence === undefined ? null : <p className="warn">{fence}</p>}

      <h4 className="site-sub">{t('site.week.hours')}</h4>
      {intake.hours === null ? (
        <Empty>{t('site.week.noHours')}</Empty>
      ) : (
        <>
          <table className="table table--dense site-week">
            <tbody>
              {intake.hours.map((day) => (
                <tr key={day.weekday}>
                  <th scope="row">{weekdayName(day.weekday, locale)}</th>
                  <td>
                    {day.closed || day.windows.length === 0 ? (
                      <span className="muted">{t('site.hours.closed')}</span>
                    ) : (
                      day.windows.map((w) => `${w.start}–${w.end}`).join(', ')
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {asking === null && hoursAction.failure !== null ? (
            <Failure failure={hoursAction.failure} />
          ) : null}
          <div className="row-actions">
            <button
              type="button"
              className="button button--primary button--sm"
              disabled={!onboarding || hoursAction.busy}
              title={fence}
              onClick={() => {
                hoursAction.clear();
                setAsking('hours');
              }}
            >
              {t('site.week.applyHours')}
            </button>
          </div>
        </>
      )}

      <h4 className="site-sub">{t('site.week.closures')}</h4>
      {intake.closures.length === 0 ? (
        <Empty>{t('site.week.noClosures')}</Empty>
      ) : (
        <>
          <ul className="plain-list">
            {intake.closures.map((closure) => (
              <li key={`${closure.from}:${closure.to}`}>{span(closure, locale)}</li>
            ))}
          </ul>
          <div className="row-actions">
            <button
              type="button"
              className="button button--primary button--sm"
              disabled={!onboarding || closuresAction.busy}
              title={fence}
              onClick={() => {
                closuresAction.clear();
                setAsking('closures');
              }}
            >
              {t('site.week.applyClosures')}
            </button>
          </div>
        </>
      )}
      {asking === null && closuresAction.failure !== null ? (
        <Failure failure={closuresAction.failure} />
      ) : null}

      {intake.applied_closures.length === 0 ? null : (
        <>
          <h4 className="site-sub">{t('site.week.written')}</h4>
          <ul className="plain-list">
            {intake.applied_closures.map((closure) => (
              <li key={closure.blackout_id}>
                {shortDate(closure.start, locale)} – {shortDate(closure.end, locale)}
                {closure.reason === '' ? '' : ` (${closure.reason})`}{' '}
                <button
                  type="button"
                  className="linkish"
                  disabled={!onboarding || closuresAction.busy}
                  onClick={() => {
                    closuresAction.clear();
                    setAsking(closure);
                  }}
                >
                  {t('common.remove')}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {asking === 'hours' && intake.hours !== null ? (
        <Confirm
          tone="primary"
          title={t('site.week.hoursTitle', { name })}
          consequence={t('site.week.hoursConsequence', {
            week: weekSentence(intake.hours, locale, t),
          })}
          confirmLabel={t('site.week.applyHours')}
          busy={hoursAction.busy}
          failure={hoursAction.failure}
          onConfirm={() => void applyHours()}
          onCancel={() => setAsking(null)}
        />
      ) : null}

      {asking === 'closures' ? (
        <Confirm
          tone="primary"
          title={t('site.week.closuresTitle', { name })}
          consequence={t('site.week.closuresConsequence', {
            days: intake.closures.map((c) => span(c, locale)).join('; '),
          })}
          confirmLabel={t('site.week.applyClosures')}
          busy={closuresAction.busy}
          failure={closuresAction.failure}
          onConfirm={() => void applyClosures()}
          onCancel={() => setAsking(null)}
        />
      ) : null}

      {asking !== null && typeof asking === 'object' ? (
        <Confirm
          title={t('site.week.removeTitle')}
          consequence={t('site.week.removeConsequence', {
            days: `${shortDate(asking.start, locale)} – ${shortDate(asking.end, locale)}`,
          })}
          confirmLabel={t('common.remove')}
          busy={closuresAction.busy}
          failure={closuresAction.failure}
          onConfirm={() => void removeClosure(asking)}
          onCancel={() => setAsking(null)}
        />
      ) : null}
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* Their menu, as a catalog file                                       */
/* ------------------------------------------------------------------ */

/**
 * **The list they typed, and the file it drafts** — moved here unchanged in
 * what it does: the rows that still need a person (never a guess — a length
 * or a price is read only where their words mean one thing), **Seed this**,
 * which hands the file to What they sell for a dry run and writes nothing,
 * and the file and command to copy for an operator who would rather use a
 * terminal. Seed is disabled while anything still needs a person, because the
 * seeder would refuse the file naming the same rows.
 */
function HerMenu({
  tenantId,
  onSeed,
}: {
  tenantId: string;
  onSeed: (fileText: string) => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const page = useRead(() => api.intakeCatalogDraft(tenantId), [tenantId]);
  const [copied, setCopied] = useState('');

  const draft = page.value;
  const items = draft === null ? 0 : countItems(draft.file);

  const copy = (what: string, value: string): void => {
    void navigator.clipboard
      ?.writeText(value)
      .then(() => setCopied(what))
      .catch(() => setCopied(''));
  };

  const ready = draft !== null && draft.parsable && draft.needs_operator.length === 0;

  return (
    <Panel
      title={t('site.menu.title')}
      className="site-card"
      actions={
        draft === null || items === 0 ? undefined : (
          <span className="muted">{t('site.menu.count', { count: items })}</span>
        )
      }
    >
      <p className="muted">{t('site.menu.lead')}</p>
      {page.failure !== null ? <Failure failure={page.failure} /> : null}
      {draft === null ? (
        page.loading ? (
          <Loading what={t('loading.intake')} />
        ) : null
      ) : items === 0 ? (
        <Empty>{t('site.menu.empty')}</Empty>
      ) : (
        <>
          {draft.needs_operator.length === 0 ? null : (
            <>
              <h4 className="site-sub">{t('site.menu.needsPerson')}</h4>
              <p className="muted">{t('site.menu.needsPersonLead')}</p>
              <table className="table table--dense">
                <tbody>
                  {draft.needs_operator.map((gap) => (
                    <tr key={`${gap.ref}:${gap.field}`}>
                      <td>
                        <code>{gap.ref}</code>
                      </td>
                      <td>{humanize(gap.field)}</td>
                      <td>
                        {gap.said === '' ? (
                          <span className="muted">{t('site.menu.saidNothing')}</span>
                        ) : (
                          t('site.menu.said', { said: gap.said })
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {draft.parsable ? null : <p className="warn">{t('site.menu.notReady')}</p>}

          <div className="row-actions">
            <button
              type="button"
              className="button button--primary button--sm"
              disabled={!ready}
              onClick={() => onSeed(draft.file_text)}
            >
              {t('intake.menu.seed')}
            </button>
            <button
              type="button"
              className="button button--sm"
              onClick={() => copy('file', draft.file_text)}
            >
              {copied === 'file' ? t('once.copied') : t('site.menu.copyFile')}
            </button>
            <button
              type="button"
              className="button button--sm"
              onClick={() => copy('command', draft.command)}
            >
              {copied === 'command' ? t('once.copied') : t('site.menu.copyCommand')}
            </button>
          </div>
          {ready ? (
            <p className="muted">{t('site.menu.seedNote')}</p>
          ) : (
            <p className="muted">{t('site.menu.seedBlocked')}</p>
          )}

          <details className="site-file">
            <summary>{t('site.menu.showFile')}</summary>
            <pre>{draft.file_text}</pre>
            <p className="muted">
              {t('site.menu.terminal')} <code>{draft.command}</code>
            </p>
          </details>
        </>
      )}
    </Panel>
  );
}

function countItems(file: unknown): number {
  if (file === null || typeof file !== 'object') return 0;
  const items = (file as Record<string, unknown>)['items'];
  return Array.isArray(items) ? items.length : 0;
}
